import { DirectiveOptions } from 'vue'

// Em dev/prod a CSP (style-src sem 'unsafe-inline') bloqueia o style="" que chega como texto de
// HTML (v-html, innerHTML), mas libera o que é aplicado pelo CSSOM (el.style). O atributo continua
// legível via getAttribute, então o estilo é reaplicado por script, só para as propriedades que o
// e-mail usa.
const PREFIXOS_PERMITIDOS = [
  'color', 'background', 'font', 'text-', 'line-height', 'letter-spacing', 'white-space',
  'margin', 'padding', 'border', 'width', 'height', 'max-width', 'min-width',
  'display', 'vertical-align', 'list-style', 'float',
]

const VALOR_PROIBIDO = /(?:url|image-set|image|cross-fade|src|element)\(|expression|@import|javascript:/i

export function valorProibido(valor: string): boolean {
  return VALOR_PROIBIDO.test(valor)
}

function propriedadePermitida(propriedade: string): boolean {
  return PREFIXOS_PERMITIDOS.some(prefixo => propriedade.startsWith(prefixo))
}

function reaplicarEstilo(elemento: HTMLElement, podar: boolean) {
  const bruto = elemento.getAttribute('style')

  if (!bruto) {
    return
  }

  elemento.style.cssText = bruto

  if (!podar) {
    return
  }

  for (let i = elemento.style.length - 1; i >= 0; i--) {
    const propriedade = elemento.style[i]

    if (!propriedadePermitida(propriedade) || valorProibido(elemento.style.getPropertyValue(propriedade))) {
      elemento.style.removeProperty(propriedade)
    }
  }
}

// podar: na prévia só se exibe, então propriedades fora da lista são descartadas; no editor o HTML
// volta para o v-model e é salvo, então nada pode ser removido.
export function aplicarEstilosInline(raiz: ParentNode, podar: boolean = true) {
  if ((raiz as HTMLElement).matches && (raiz as HTMLElement).matches('[style]')) {
    reaplicarEstilo(raiz as HTMLElement, podar)
  }

  raiz.querySelectorAll<HTMLElement>('[style]').forEach(elemento => reaplicarEstilo(elemento, podar))
}

// Para o Quill: o vue2-editor carrega o conteúdo com root.innerHTML = html, e o Quill lê
// color/background de node.style ao adotar os nós. Se o estilo estiver bloqueado, a cor some do
// Delta e não volta no v-model. Aqui o HTML é parseado num template, recebe o estilo pelo CSSOM e
// só então entra no editor, de modo que o Quill já enxerga os estilos.
export function instalarInnerHtmlCompativelComCsp(raiz: HTMLElement) {
  if (Object.prototype.hasOwnProperty.call(raiz, 'innerHTML')) {
    return
  }

  const original = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML')

  if (!original || !original.get || !original.set) {
    return
  }

  const { get, set } = original

  Object.defineProperty(raiz, 'innerHTML', {
    configurable: true,
    get() {
      return get.call(this)
    },
    set(html: string) {
      const modelo = document.createElement('template')
      modelo.innerHTML = html
      aplicarEstilosInline(modelo.content, false)
      set.call(this, '')
      this.appendChild(modelo.content)
    },
  })
}

// O Firefox descarta o atributo style bloqueado ao entrar por innerHTML (o Chrome o mantém), então
// reaplicar depois do v-html não tem de onde ler. A diretiva recebe o HTML e o renderiza parseado
// num template, onde o atributo ainda existe, antes de inserir no elemento. É um sink de HTML cru
// como o v-html: o valor precisa chegar já sanitizado ($sanitize).
function renderizarHtmlComEstilos(el: HTMLElement, html: string) {
  const modelo = document.createElement('template')
  modelo.innerHTML = html || ''
  aplicarEstilosInline(modelo.content)

  el.textContent = ''
  el.appendChild(modelo.content)
}

export const estiloInlineDirective: DirectiveOptions = {
  bind(el, binding) {
    renderizarHtmlComEstilos(el, binding.value)
  },
  update(el, binding) {
    if (binding.value !== binding.oldValue) {
      renderizarHtmlComEstilos(el, binding.value)
    }
  },
}
